import { UI2IconName, UI2IconNames, UI2_ICON_NAMES } from './icon-names'

/**
 * How one semantic name renders. `lucide` is the default on every platform —
 * lucide is the single glyph source, so NM/DW/MW draw the same shape rather
 * than three lookalikes from three icon sets.
 *
 * The asset fields are an escape hatch for glyphs lucide genuinely lacks
 * (bundled SVG/PNG artwork). Nothing uses them yet; keep it that way unless
 * lucide has no equivalent at all.
 */
export interface UI2IconDefinition {
  /** Lucide export name, PascalCase. Must exist in BOTH lucide-react and lucide-react-native. */
  lucide: string
  /** Platform asset keys, used instead of `lucide` when set. */
  webAsset?: string
  nativeAsset?: string
}

export const UI2_ICON_REGISTRY: Record<UI2IconName, UI2IconDefinition> = {
  [UI2IconNames.CHEVRON_RIGHT]: { lucide: 'ChevronRight' },
  [UI2IconNames.CHEVRON_LEFT]: { lucide: 'ChevronLeft' },
  [UI2IconNames.CHEVRON_DOWN]: { lucide: 'ChevronDown' },
  [UI2IconNames.CHEVRON_UP]: { lucide: 'ChevronUp' },
  [UI2IconNames.ARROW_RIGHT]: { lucide: 'ArrowRight' },
  [UI2IconNames.ARROW_LEFT]: { lucide: 'ArrowLeft' },
  [UI2IconNames.EXTERNAL_LINK]: { lucide: 'ExternalLink' },

  [UI2IconNames.X]: { lucide: 'X' },
  [UI2IconNames.CIRCLE_X]: { lucide: 'CircleX' },
  [UI2IconNames.CHECK]: { lucide: 'Check' },
  [UI2IconNames.CIRCLE_CHECK]: { lucide: 'CircleCheck' },
  [UI2IconNames.PLUS]: { lucide: 'Plus' },
  [UI2IconNames.PENCIL]: { lucide: 'Pencil' },
  [UI2IconNames.TRASH]: { lucide: 'Trash2' },
  [UI2IconNames.DOWNLOAD]: { lucide: 'Download' },
  [UI2IconNames.UPLOAD]: { lucide: 'Upload' },
  [UI2IconNames.PRINTER]: { lucide: 'Printer' },
  [UI2IconNames.SEARCH]: { lucide: 'Search' },
  [UI2IconNames.FILTER]: { lucide: 'Filter' },
  [UI2IconNames.REFRESH_CW]: { lucide: 'RefreshCw' },
  [UI2IconNames.ROTATE_CCW]: { lucide: 'RotateCcw' },
  [UI2IconNames.ZOOM_IN]: { lucide: 'ZoomIn' },
  [UI2IconNames.MAXIMIZE]: { lucide: 'Maximize2' },
  [UI2IconNames.SHARE]: { lucide: 'Share2' },

  [UI2IconNames.TRIANGLE_ALERT]: { lucide: 'TriangleAlert' },
  [UI2IconNames.CIRCLE_ALERT]: { lucide: 'CircleAlert' },
  [UI2IconNames.INFO]: { lucide: 'Info' },
  [UI2IconNames.CLOUD_OFF]: { lucide: 'CloudOff' },
  [UI2IconNames.LOCK]: { lucide: 'Lock' },
  [UI2IconNames.EYE]: { lucide: 'Eye' },
  [UI2IconNames.EYE_OFF]: { lucide: 'EyeOff' },

  [UI2IconNames.CALENDAR]: { lucide: 'Calendar' },
  [UI2IconNames.CLOCK]: { lucide: 'Clock' },
  [UI2IconNames.MAP_PIN]: { lucide: 'MapPin' },
  [UI2IconNames.LOCATE_FIXED]: { lucide: 'LocateFixed' },

  [UI2IconNames.CAMERA]: { lucide: 'Camera' },
  [UI2IconNames.IMAGE]: { lucide: 'Image' },
  [UI2IconNames.FILM]: { lucide: 'Film' },
  [UI2IconNames.SCAN_BARCODE]: { lucide: 'ScanBarcode' },
  [UI2IconNames.SCAN_QR_CODE]: { lucide: 'ScanQrCode' },
  [UI2IconNames.SCAN_TEXT]: { lucide: 'ScanText' },
  [UI2IconNames.QR_CODE]: { lucide: 'QrCode' },

  [UI2IconNames.FILE]: { lucide: 'File' },
  [UI2IconNames.FILE_TEXT]: { lucide: 'FileText' },
  [UI2IconNames.FILE_ARCHIVE]: { lucide: 'FileArchive' },
  [UI2IconNames.TABLE]: { lucide: 'Table' },
  [UI2IconNames.PAPERCLIP]: { lucide: 'Paperclip' },

  [UI2IconNames.TRUCK]: { lucide: 'Truck' },
  [UI2IconNames.PACKAGE]: { lucide: 'Package' },
  [UI2IconNames.USER]: { lucide: 'User' },
}

/** Default glyph size, in px on web and pt on native. */
export const UI2_ICON_DEFAULT_SIZE = 16

export function isUI2IconName(name: any): name is UI2IconName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(UI2_ICON_REGISTRY, name)
}

export function resolveUI2Icon(name: any): UI2IconDefinition | undefined {
  return isUI2IconName(name) ? UI2_ICON_REGISTRY[name] : undefined
}

export const UI2_ICON_LUCIDE_NAMES: string[] = Array.from(
  new Set(UI2_ICON_NAMES.map((name) => UI2_ICON_REGISTRY[name].lucide))
)
